import { Container, Graphics, Rectangle, Text } from "pixi.js"

import { BUTTON_SIZES } from "../ui_tokens"
import { DISPLAY_FONT_FAMILY, TextRole, trackLetters } from "../../text/typography"
import { Tween, tween } from "../../intro/intro_motion"
import { lerpColor } from "../../intro/logo_choreography"
import { createIconButton } from "../button_factory"
import { UiButton } from "../ui_button"
import { TextRenderer } from "../../text/text_renderer"
import {
    ACCENT,
    CARD_FILL,
    CARD_HOVER_FILL,
    CARD_RIM,
    PANEL_ALPHA,
    PANEL_FILL,
    RIM,
    RIM_DIM,
    SELECTED_FILL,
    TEXT,
    TEXT_DIM,
} from "./theme"

// The small set of pieces the character-creation screen is built from. Everything is drawn by hand
// (no filters -- see the budget-hardware note): glows are a fatter low-alpha copy of the shape
// underneath, which costs one extra draw call instead of an offscreen render pass.

export const text = (
    value: string,
    {
        role = "compactLabel",
        color = TEXT,
        bold = false,
        wrap,
        display = false,
        weight,
        size,
    }: {
        role?: TextRole
        color?: number
        bold?: boolean
        wrap?: number
        // The display face (Jost) -- headings and the story's prose, so this screen speaks in the
        // same voice as the loading screen and the login card before it. Labels and chips stay in
        // the UI face.
        display?: boolean
        // A numeric weight for the display face ("400", "500"); `bold` is the UI face's on/off.
        weight?: string
        size?: number
    } = {},
): Text => {
    // No black outline. Every label on this screen sits on a flat panel or chip, so the stroke buys
    // nothing -- and on the light fills (a primary pill, a selected chip, a numbered badge) it wraps
    // the dark ink in a halo of its own colour, which smears small bold type into a blur.
    const renderer = new TextRenderer().color(color).role(role).bold(bold).strokeThickness(0)
    if (display) {
        renderer.fontFamily(DISPLAY_FONT_FAMILY)
    }
    if (size !== undefined) {
        renderer.fontSize(size)
    }
    if (wrap !== undefined) {
        // Set at build time: poking style.wordWrap afterwards re-rasterizes the Text.
        renderer.wordWrap(wrap)
    }
    const label = renderer.render(value) as Text
    if (weight) {
        // Before anything reads .width: a weight change re-rasterizes, and weights differ in advance.
        label.style.fontWeight = weight as any
    }
    return label
}

/** Letter-spaced display caps -- a heading, a kicker. See trackLetters for why not letterSpacing. */
export const capsText = (value: string, options: Parameters<typeof text>[1] = {}): Text =>
    text(trackLetters(value.toUpperCase()), { display: true, weight: "500", ...options })

/**
 * A hairline that swells in the middle and tapers to nothing at both ends, with a soft glow around it
 * -- the same lens the login wordmark draws under itself. One path per layer: segments with falling
 * alpha leave bright seams where they overlap.
 */
export const lensRule = (g: Graphics, cx: number, y: number, halfWidth: number, color: number = RIM, alpha: number = 1) => {
    const lens = (half: number) => {
        g.moveTo(cx - halfWidth, y)
        g.quadraticCurveTo(cx, y - half * 2, cx + halfWidth, y)
        g.quadraticCurveTo(cx, y + half * 2, cx - halfWidth, y)
        g.closePath()
    }
    g.beginFill(color, 0.14 * alpha)
    lens(3)
    g.endFill()
    g.beginFill(ACCENT, 0.85 * alpha)
    lens(0.9)
    g.endFill()
}

/**
 * Hover that EASES rather than snaps: a 0..1 amount that glides toward the pointer's state and
 * repaints as it goes, so sweeping across a palette reads as the chips answering rather than
 * flickering between two pictures.
 */
const HOVER_MS = 120
class HoverEase {
    value = 0
    private motion?: Tween

    constructor(private repaint: () => void) {}

    set = (hovered: boolean) => {
        this.motion?.cancel()
        const from = this.value
        const to = hovered ? 1 : 0
        this.motion = tween(HOVER_MS * Math.abs(to - from), t => {
            this.value = from + (to - from) * t
            this.repaint()
        })
    }

    stop = () => this.motion?.cancel()
}

// Shrink, then clip, a label to fit a box. Proportional text can't be sized from a character count,
// so measure and act on the real width.
export const fitText = (label: Text, maxWidth: number, minScale = 0.78) => {
    if (label.width <= maxWidth || label.width === 0) {
        return
    }
    const scale = Math.max(minScale, maxWidth / label.width)
    label.scale.set(scale)
    if (label.width <= maxWidth) {
        return
    }
    const source = label.text
    for (let cut = source.length - 1; cut > 1; cut--) {
        label.text = `${source.slice(0, cut).trimEnd()}…`
        if (label.width <= maxWidth) {
            return
        }
    }
}

export const centerText = (label: Text, boxX: number, boxWidth: number, y: number) => {
    label.x = Math.round(boxX + (boxWidth - label.width) / 2)
    label.y = Math.round(y)
}

// Dark glass with a warm rim and a faint halo -- the panel every column of the screen sits in.
export const drawGlassPanel = (
    g: Graphics,
    x: number,
    y: number,
    w: number,
    h: number,
    { radius = 10, rim = RIM_DIM, rimAlpha = 0.75, glow = true, fill = PANEL_FILL, fillAlpha = PANEL_ALPHA } = {},
) => {
    if (glow) {
        g.beginFill(RIM, 0.05)
        g.drawRoundedRect(x - 4, y - 4, w + 8, h + 8, radius + 4)
        g.endFill()
    }
    g.lineStyle(1.5, rim, rimAlpha)
    g.beginFill(fill, fillAlpha)
    g.drawRoundedRect(x, y, w, h, radius)
    g.endFill()
    g.lineStyle(0)
    // Light catching the top lip, inset past the corners: glass with an edge, not a flat cut-out.
    g.beginFill(TEXT, 0.07)
    g.drawRect(x + radius, y + 1.5, w - radius * 2, 1)
    g.endFill()
}

// A section heading inside a panel: small caps-ish label with a hairline rule running off to the
// right, which reads as structure without spending vertical space on a box.
export const sectionHeading = (title: string, width: number): Container => {
    const container = new Container()
    const label = text(title.toUpperCase(), { role: "compactCaption", color: ACCENT, bold: true })
    label.alpha = 0.9
    container.addChild(label)

    const rule = new Graphics()
    rule.beginFill(RIM_DIM, 0.5)
    rule.drawRect(label.width + 8, Math.round(label.height / 2), Math.max(0, width - label.width - 8), 1)
    rule.endFill()
    container.addChild(rule)

    return container
}

interface ChoiceProps {
    width: number
    height: number
    selected: boolean
    onClick: () => void
    // Hovering a choice previews nothing by itself; it just lifts the chip.
    tooltip?: string
}

// One labelled option in a palette (a hairstyle, a cut of shirt, male/female...).
export class LabelChip extends Container {
    private bg: Graphics
    private hover = new HoverEase(() => this.draw())
    private readonly props: ChoiceProps

    constructor(label: string, props: ChoiceProps) {
        super()
        this.props = props
        this.bg = new Graphics()
        this.addChild(this.bg)

        const caption = text(label, { role: "compactCaption", color: props.selected ? 0x2a1c0c : TEXT })
        fitText(caption, props.width - 12)
        centerText(caption, 0, props.width, (props.height - caption.height) / 2)
        this.addChild(caption)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, props.width, props.height)
        this.on("pointerover", () => this.hover.set(true))
        this.on("pointerout", () => this.hover.set(false))
        this.on("removed", this.hover.stop)
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw()
    }

    private draw() {
        const { width, height, selected } = this.props
        const h = this.hover.value
        const g = this.bg
        g.clear()
        if (selected) {
            g.beginFill(ACCENT, 0.18)
            g.drawRoundedRect(-3, -3, width + 6, height + 6, 9)
            g.endFill()
            g.lineStyle(1.5, ACCENT, 1)
            g.beginFill(ACCENT, 0.92)
        } else {
            if (h > 0) {
                g.beginFill(RIM, 0.12 * h)
                g.drawRoundedRect(-3, -3, width + 6, height + 6, 9)
                g.endFill()
            }
            g.lineStyle(1, lerpColor(CARD_RIM, RIM, h), 0.6 + 0.3 * h)
            g.beginFill(lerpColor(CARD_FILL, CARD_HOVER_FILL, h), 0.95)
        }
        g.drawRoundedRect(0, 0, width, height, 6)
        g.endFill()
        g.lineStyle(0)
    }
}

// One colour in a palette. The swatch is the whole point, so the name only appears on hover.
export class ColorChip extends Container {
    private bg: Graphics
    private hover = new HoverEase(() => this.draw())
    private readonly props: ChoiceProps & { color: number }

    constructor(color: number, props: ChoiceProps) {
        super()
        this.props = { ...props, color }
        this.bg = new Graphics()
        this.addChild(this.bg)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, props.width, props.height)
        this.on("pointerover", () => this.hover.set(true))
        this.on("pointerout", () => this.hover.set(false))
        this.on("removed", this.hover.stop)
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw()
    }

    private draw() {
        const { width, height, selected, color } = this.props
        const h = this.hover.value
        const g = this.bg
        g.clear()
        if (selected || h > 0) {
            g.beginFill(selected ? ACCENT : RIM, selected ? 0.3 : 0.16 * h)
            g.drawRoundedRect(-4, -4, width + 8, height + 8, 9)
            g.endFill()
        }
        g.lineStyle(selected ? 2 : 1, selected ? ACCENT : lerpColor(0x000000, RIM, h), selected ? 1 : 0.55 + 0.35 * h)
        g.beginFill(color, 1)
        g.drawRoundedRect(0, 0, width, height, 5)
        g.endFill()
        // Inner highlight: a hairline of light along the top edge so flat colours read as objects.
        g.lineStyle(1, 0xffffff, 0.18)
        g.moveTo(3, 2)
        g.lineTo(width - 3, 2)
        g.lineStyle(0)
    }
}

// A trait in the left-hand list: icon, name, and the choice currently made.
export class TraitRow extends Container {
    private bg: Graphics
    private hover: HoverEase

    constructor(props: {
        icon: string
        label: string
        summary: string
        swatch?: number
        width: number
        height: number
        selected: boolean
        onClick: () => void
    }) {
        super()
        const { width, height, selected } = props
        this.bg = new Graphics()
        this.addChild(this.bg)

        const icon = text(props.icon, { role: "caption" })
        icon.x = 9
        icon.y = Math.round((height - icon.height) / 2)
        this.addChild(icon)

        const name = text(props.label, { role: "compactLabel", color: selected ? 0xfff6e5 : TEXT, bold: selected })
        name.x = 32
        name.y = Math.round((height - name.height) / 2) - 5
        this.addChild(name)

        const summary = text(props.summary, { role: "compactCaption", color: selected ? ACCENT : TEXT_DIM })
        const summaryRight = props.swatch !== undefined ? width - 24 : width - 10
        fitText(summary, summaryRight - 32)
        summary.x = 32
        summary.y = name.y + 13
        this.addChild(summary)

        if (props.swatch !== undefined) {
            const dot = new Graphics()
            dot.lineStyle(1, 0x000000, 0.4)
            dot.beginFill(props.swatch, 1)
            dot.drawCircle(width - 15, height / 2, 6)
            dot.endFill()
            this.addChild(dot)
        }

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, width, height)
        this.hover = new HoverEase(() => this.draw(props))
        this.on("pointerover", () => this.hover.set(true))
        this.on("pointerout", () => this.hover.set(false))
        this.on("removed", this.hover.stop)
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw(props)
    }

    private draw(props: { width: number; height: number; selected: boolean }) {
        const { width, height, selected } = props
        const h = this.hover.value
        const g = this.bg
        g.clear()
        if (selected) {
            g.beginFill(SELECTED_FILL, 0.95)
            g.drawRoundedRect(0, 0, width, height, 7)
            g.endFill()
            // Amber spine on the selected row, pointing at the palette it opened.
            g.beginFill(RIM, 1)
            g.drawRoundedRect(0, 4, 3, height - 8, 1.5)
            g.endFill()
        } else if (h > 0) {
            g.beginFill(CARD_HOVER_FILL, 0.8 * h)
            g.drawRoundedRect(0, 0, width, height, 7)
            g.endFill()
            // The spine starts to show on hover, so the row hints at what clicking it will do.
            g.beginFill(RIM, 0.45 * h)
            g.drawRoundedRect(0, 4 + (height - 8) * (1 - h) * 0.5, 3, (height - 8) * h, 1.5)
            g.endFill()
        }
    }
}

// Footer actions share button rendering and input; the footer still owns their arrangement.
export class PillButton extends UiButton {
    readonly boxWidth: number

    constructor(props: { label: string; primary?: boolean; onClick: () => void; height?: number }) {
        super({ label: props.label, onClick: props.onClick, theme: "ember", size: "small",
            variant: props.primary ? "primary" : "secondary", appearance: props.primary ? "filled" : "outline",
            tone: props.primary ? "accent" : "neutral", shape: "pill", bold: !!props.primary, height: props.height ?? BUTTON_SIZES.small.height, hitPadding: { x: 4, y: 10 } })
        this.boxWidth = this.getBoxSize().width
        this.resize(this.boxWidth, props.height ?? BUTTON_SIZES.small.height)
    }
}

// "‹ 2 / 4 ›" for a palette too big for one page.
export class Pager extends Container {
    readonly boxWidth: number

    constructor(props: { page: number; pages: number; onPage: (page: number) => void }) {
        super()
        const { page, pages, onPage } = props

        const arrow = (glyph: string, enabled: boolean, target: number) => createIconButton({
            icon: glyph, theme: "ember", width: BUTTON_SIZES.extraSmall.height, height: BUTTON_SIZES.extraSmall.height, size: "extraSmall", enabled,
            onClick: () => onPage(target),
        })

        const left = arrow("‹", page > 0, page - 1)
        this.addChild(left)

        const counter = text(`${page + 1} / ${pages}`, { role: "compactCaption", color: TEXT_DIM })
        counter.x = 24
        counter.y = Math.round((BUTTON_SIZES.extraSmall.height - counter.height) / 2)
        this.addChild(counter)

        const right = arrow("›", page < pages - 1, page + 1)
        right.x = 28 + counter.width
        this.addChild(right)

        this.boxWidth = right.x + 18
    }
}
